import GroupsIcon from "@mui/icons-material/Groups"
import {
    Avatar,
    Box,
    Divider,
    Stack,
    Typography,
    useTheme,
} from "@mui/material"
import { ReactElement, useState } from "react"
import { useNavigate } from "react-router-dom"

import AppButton from "../../components/buttons/appButton"
import VerticalKeyValueList from "../../components/dataDisplay/verticalKeyValueList"
import ErrorDisplay from "../../components/fallback/errorDisplay"
import MyOrgSkeleton from "../../components/loading/skeletons/myOrgSkeleton"
import CompanyDetailsPopup from "../../components/popUps/companyDetails/companyDetailsPopup"
import InviteUser from "../../components/popUps/inviteUser/inviteUser"
import AppPaper from "../../components/surfaces/appPaper"
import DisplayIf from "../../components/wrappers/displayIf"
import { useScreenSize } from "../../hooks/useScreenSize"
import { ADMIN_PORTAL } from "../../router/routes"
import useGetMyOrg from "../../services/middleware/hooks/myOrganisation/useGetMyOrg"

const TEXT_PADDING = 3

const MyOrganisation = (): ReactElement => {
    const [openPopup, setOpenPopup] = useState(false)
    const { isLoading, error, data, mutate } = useGetMyOrg()
    const [showInviteUser, setShowInviteUser] = useState(false)
    const navigate = useNavigate()
    const theme = useTheme()
    const { isMob } = useScreenSize()
    if (error) {
        return <ErrorDisplay error={error} />
    }

    if (isLoading) {
        return <MyOrgSkeleton />
    }

    const isDomains = data?.organisation?.domains
        ? data?.organisation?.domains.length > 0
        : false

    return (
        <Box
            width="100%"
            display="flex"
            justifyContent="center"
            flexDirection="row"
        >
            <AppPaper sx={{ width: { xs: "100%", md: "60%" } }}>
                <Stack>
                    <Stack direction="row" spacing={3}>
                        <DisplayIf condition={!isMob}>
                            <Avatar
                                sx={{
                                    background:
                                        theme.palette.mode === "dark"
                                            ? theme.palette.background
                                                  .contentBackground
                                            : theme.palette.primary.light,
                                    width: "120px",
                                    height: "120px",
                                    mr: 1,
                                }}
                            >
                                <GroupsIcon
                                    sx={{ fontSize: "80px" }}
                                    color="primary"
                                />
                            </Avatar>
                        </DisplayIf>
                        <div>
                            <Typography variant="h4" fontWeight={500}>
                                My Organisation
                            </Typography>
                            <Typography variant="h6" fontWeight={400}>
                                A summary and command center for managing your
                                team on ENA Connect Direct. From here, you can
                                invite users, link to your user summary page
                            </Typography>
                        </div>
                    </Stack>
                    <Stack
                        direction={{ xs: "column", sm: "row" }}
                        justifyContent="flex-end"
                        spacing={2}
                        paddingTop={2}
                        paddingBottom={TEXT_PADDING}
                    >
                        <AppButton
                            variant="outlined"
                            onClick={() => navigate(ADMIN_PORTAL)}
                        >
                            Mange Existing Users
                        </AppButton>
                        <AppButton
                            variant="contained"
                            onClick={() => setShowInviteUser(true)}
                        >
                            Invite New User
                        </AppButton>
                        <AppButton
                            variant="contained"
                            onClick={() => setOpenPopup(true)}
                            color="secondary"
                        >
                            Edit Organisation
                        </AppButton>
                    </Stack>
                    <CompanyDetailsPopup
                        isOpen={openPopup}
                        isEditOrganisation
                        onSubmit={() => {
                            setOpenPopup(false)
                            mutate()
                        }}
                        data={data}
                        setIsOpen={setOpenPopup}
                        isAdmin
                    />
                    <Divider flexItem />
                    <Box paddingY={TEXT_PADDING}>
                        <VerticalKeyValueList
                            kwargs={{
                                Name:
                                    data?.organisation.organisationName ||
                                    "We do not have a name for your organisation",
                            }}
                        />
                    </Box>
                    <Divider flexItem />
                    <Box paddingY={TEXT_PADDING}>
                        <VerticalKeyValueList
                            kwargs={{
                                Address:
                                    data?.organisation.organisationAddress ||
                                    "We do not have an address for your organisation. Please raise a support ticket to get this added",
                            }}
                        />
                    </Box>
                    <Divider flexItem />
                    <Box paddingY={TEXT_PADDING}>
                        <VerticalKeyValueList
                            kwargs={{
                                Postcode:
                                    data?.organisation.organisationPostcode ||
                                    "We do not have a postcode for your organisation. Please raise a support ticket to get this added",
                            }}
                        />
                    </Box>
                    <Divider flexItem />
                    <Box paddingY={TEXT_PADDING}>
                        <VerticalKeyValueList
                            kwargs={{
                                "Contact Number":
                                    data?.organisation
                                        .organisationContactNumber ||
                                    "We do not have a contact number for your organisation. Please raise a support ticket to get this added",
                            }}
                        />
                    </Box>

                    <Divider flexItem />
                    <Box paddingY={TEXT_PADDING}>
                        <VerticalKeyValueList
                            kwargs={{
                                "Email Domain": isDomains
                                    ? data?.organisation?.domains
                                          .map((domain) => `@${domain}`)
                                          .join(" ")
                                    : "No associated email domains",
                            }}
                        />
                        <Typography
                            variant="body2"
                            color="text.disabled"
                            marginTop={1}
                            width={{ xs: "100%", md: "40%" }}
                        >
                            Any users who sign up with this domain will be
                            auto-routed into your team. You must approve new
                            accounts before they can perform actions. To add or
                            remove domains, please contact our support team
                        </Typography>
                    </Box>
                </Stack>
            </AppPaper>

            <InviteUser isOpen={showInviteUser} setIsOpen={setShowInviteUser} />
        </Box>
    )
}

export default MyOrganisation
